// Palettes people already love in their editors and terminals, each given a
// place to live. They are made from the same parts as a theme someone asks
// their agent for, so they double as examples of what a theme can be.

import type { CustomTheme } from "./types";
import { PICTURE_THEMES } from "./pictureThemes";

const at = (t: Omit<CustomTheme, "createdAt" | "updatedAt" | "by">): CustomTheme => ({ ...t, by: "Context Board", createdAt: 0, updatedAt: 0 });

export const PALETTES: CustomTheme[] = [
  at({
    id: "p:tokyo-night",
    name: "Tokyo Night",
    blurb: "Neon on a harbor city in the rain.",
    mode: "dark",
    font: "clean",
    colors: { bg: "#13141f", tile: "#1f2335", ink: "#c0caf5", ink2: "#a9b1d6", ink3: "#737aa2", accent: "#7aa2f7", ok: "#9ece6a", warn: "#e0af68", bad: "#f7768e", charts: ["#7aa2f7", "#bb9af7", "#9ece6a", "#e0af68", "#7dcfff"] },
    glass: 0.3,
    blur: 14,
    scene: {
      style: "pixel",
      seed: 412,
      sky: ["#0c0d16", "#12142a", "#191c3a", "#24284f", "#33366a"],
      horizon: 0.76,
      body: { kind: "moon", x: 0.82, y: 0.15, size: 0.04, color: "#c0caf5", glow: "#5a6aaa" },
      stars: { density: 0.18, color: "#a9b1d6" },
      layers: [
        { kind: "city", color: "#1c1f36", light: "#2a2f52", at: 0.74, height: 0.34, windows: "#bb9af7" },
        { kind: "city", color: "#111220", light: "#1f2238", at: 0.78, height: 0.22, windows: "#e0af68" },
      ],
      water: { color: "#151729", light: "#7aa2f7", at: 0.79 },
      fog: { color: "#3b4272", at: 0.74, amount: 0.35 },
      particles: { kind: "rain", color: "#7aa2f7", density: 0.55 },
    },
  }),
  at({
    id: "p:catppuccin",
    name: "Catppuccin",
    blurb: "Soft pastels on a cozy night, cherry trees in bloom.",
    mode: "dark",
    font: "clean",
    colors: { bg: "#11111b", tile: "#1e1e2e", ink: "#cdd6f4", ink2: "#bac2de", ink3: "#7f849c", accent: "#cba6f7", ok: "#a6e3a1", warn: "#f9e2af", bad: "#f38ba8", charts: ["#cba6f7", "#89b4fa", "#a6e3a1", "#fab387", "#f5c2e7"] },
    glass: 0.28,
    blur: 14,
    scene: {
      style: "pixel",
      seed: 77,
      sky: ["#11111b", "#17142a", "#231c3d", "#3a2c58", "#5e4880", "#8a6aa8"],
      horizon: 0.64,
      body: { kind: "moon", x: 0.78, y: 0.2, size: 0.065, color: "#f5e0dc", glow: "#b48ad8" },
      stars: { density: 0.55, color: "#f5e0dc" },
      clouds: { color: "#4a3a68", light: "#8a6aa8", shade: "#2e2546", count: 4, at: 0.34, speed: 0.6 },
      layers: [
        { kind: "mountains", color: "#4f4170", light: "#725c9c", at: 0.63, height: 0.19, rough: 0.5 },
        { kind: "hills", color: "#342a4e", light: "#4b3e70", at: 0.7, height: 0.09, rough: 0.35 },
        { kind: "hills", color: "#29213d", light: "#3c3159", at: 0.77, height: 0.07, rough: 0.3, trees: { kind: "cherry", color: "#e8a8d4", light: "#f5c2e7", density: 0.55 } },
        { kind: "plain", color: "#1d1830", light: "#2c2545", at: 0.86, height: 0.03 },
      ],
      flowers: ["#f5c2e7", "#cba6f7", "#89b4fa", "#a6e3a1", "#fab387"],
      particles: { kind: "petals", color: "#f5c2e7", density: 0.35 },
    },
  }),
  at({
    id: "p:catppuccin-latte",
    name: "Catppuccin Latte",
    blurb: "The same pastels in daylight, under cherry blossoms.",
    mode: "light",
    font: "clean",
    colors: { bg: "#dce0e8", tile: "#eff1f5", ink: "#4c4f69", ink2: "#5c5f77", ink3: "#7c7f93", accent: "#8839ef", ok: "#40a02b", warn: "#c27a12", bad: "#d20f39", charts: ["#8839ef", "#1e66f5", "#40a02b", "#fe640b", "#ea76cb"] },
    glass: 0.22,
    blur: 12,
    scene: {
      style: "pixel",
      seed: 211,
      sky: ["#9fbdf2", "#b3cbf5", "#c8d9f8", "#dce6fa", "#eef2fc"],
      horizon: 0.66,
      body: { kind: "sun", x: 0.16, y: 0.16, size: 0.05, color: "#fff4d6", glow: "#fffaf0" },
      stars: { density: 0 },
      clouds: { color: "#ffffff", light: "#ffffff", shade: "#dbe3f7", count: 6, at: 0.26, speed: 0.8 },
      layers: [
        { kind: "hills", color: "#c7cde0", light: "#d7dcea", at: 0.68, height: 0.11 },
        { kind: "hills", color: "#a8c79a", light: "#bcd8ae", at: 0.77, height: 0.07, trees: { kind: "cherry", color: "#ea76cb", light: "#f5bde6", density: 0.55 } },
        { kind: "plain", color: "#8fb980", light: "#a5cc95", at: 0.88, height: 0.03 },
      ],
      flowers: ["#ffffff", "#ea76cb", "#df8e1d", "#8839ef", "#1e66f5"],
      particles: { kind: "petals", color: "#ea76cb", density: 0.35 },
    },
  }),
  at({
    id: "p:gruvbox",
    name: "Gruvbox",
    blurb: "Retro browns and an autumn sun over the pines.",
    mode: "dark",
    font: "mono",
    colors: { bg: "#1d2021", tile: "#282828", ink: "#ebdbb2", ink2: "#d5c4a1", ink3: "#a89984", accent: "#fe8019", ok: "#b8bb26", warn: "#fabd2f", bad: "#fb4934", charts: ["#fe8019", "#fabd2f", "#b8bb26", "#83a598", "#d3869b"] },
    glass: 0.2,
    blur: 10,
    scene: {
      style: "pixel",
      seed: 333,
      sky: ["#1d2021", "#32302f", "#504945", "#7c4a2a", "#af5a1c", "#d79921"],
      horizon: 0.66,
      body: { kind: "sun", x: 0.62, y: 0.58, size: 0.1, color: "#fabd2f", glow: "#fe8019" },
      stars: { density: 0.12, color: "#ebdbb2" },
      layers: [
        { kind: "mountains", color: "#504945", light: "#665c54", at: 0.64, height: 0.17, rough: 0.5 },
        { kind: "forest", color: "#32302f", light: "#3c3836", at: 0.75, height: 0.08, trees: { kind: "pine", color: "#1d2021", light: "#3c3836", density: 0.9 } },
        { kind: "plain", color: "#1d2021", light: "#282828", at: 0.88, height: 0.02 },
      ],
      particles: { kind: "leaves", color: "#d65d0e", density: 0.3 },
    },
  }),
  at({
    id: "p:nord",
    name: "Nord",
    blurb: "An arctic fjord, snow falling, the aurora overhead.",
    mode: "dark",
    font: "clean",
    colors: { bg: "#242933", tile: "#2e3440", ink: "#eceff4", ink2: "#d8dee9", ink3: "#a3abbd", accent: "#88c0d0", ok: "#a3be8c", warn: "#ebcb8b", bad: "#bf616a", charts: ["#88c0d0", "#81a1c1", "#a3be8c", "#ebcb8b", "#b48ead"] },
    glass: 0.3,
    blur: 14,
    scene: {
      style: "pixel",
      seed: 902,
      sky: ["#1a1e26", "#20252f", "#2a303c", "#353d4c", "#434c5e"],
      horizon: 0.7,
      body: { kind: "none" },
      stars: { density: 0.55, color: "#e5e9f0" },
      aurora: { colors: ["#8fbcbb", "#a3be8c", "#b48ead"], strength: 0.7 },
      layers: [
        { kind: "mountains", color: "#4c566a", light: "#5d6880", at: 0.7, height: 0.22, rough: 0.7, snow: "#e5e9f0" },
        { kind: "mountains", color: "#3b4252", light: "#4c566a", at: 0.72, height: 0.1, rough: 0.5, snow: "#d8dee9" },
      ],
      water: { color: "#2b313c", light: "#88c0d0", at: 0.73 },
      particles: { kind: "snow", color: "#eceff4", density: 0.35 },
    },
  }),
  at({
    id: "p:rose-pine",
    name: "Rosé Pine",
    blurb: "Dusk in the pines, the sky going rose.",
    mode: "dark",
    font: "editorial",
    colors: { bg: "#191724", tile: "#1f1d2e", ink: "#e0def4", ink2: "#b9b5d0", ink3: "#908caa", accent: "#ebbcba", ok: "#9ccfd8", warn: "#f6c177", bad: "#eb6f92", charts: ["#ebbcba", "#c4a7e7", "#9ccfd8", "#f6c177", "#31748f"] },
    glass: 0.3,
    blur: 14,
    scene: {
      style: "pixel",
      seed: 515,
      sky: ["#191724", "#232136", "#393552", "#5f4a6e", "#a0727f", "#ea9a97"],
      horizon: 0.68,
      body: { kind: "sun", x: 0.3, y: 0.6, size: 0.08, color: "#f6c177", glow: "#ea9a97" },
      stars: { density: 0.25, color: "#e0def4" },
      layers: [
        { kind: "hills", color: "#393552", light: "#44415a", at: 0.68, height: 0.1, rough: 0.3 },
        { kind: "forest", color: "#26233a", light: "#2a273f", at: 0.77, height: 0.07, trees: { kind: "pine", color: "#191724", light: "#26233a", density: 0.9 } },
        { kind: "plain", color: "#191724", light: "#1f1d2e", at: 0.9, height: 0.02 },
      ],
      particles: { kind: "fireflies", color: "#f6c177", density: 0.35 },
    },
  }),
  at({
    id: "p:kanagawa",
    name: "Kanagawa",
    blurb: "Mount Fuji over the sea, ink-wash blues, sakura on the wind.",
    mode: "dark",
    font: "editorial",
    colors: { bg: "#16161d", tile: "#1f1f28", ink: "#dcd7ba", ink2: "#c8c093", ink3: "#9a9a85", accent: "#7e9cd8", ok: "#98bb6c", warn: "#e6c384", bad: "#e46876", charts: ["#7e9cd8", "#e6c384", "#98bb6c", "#d27e99", "#7fb4ca"] },
    glass: 0.28,
    blur: 12,
    scene: {
      style: "pixel",
      seed: 1760,
      sky: ["#16161d", "#1c1c26", "#223249", "#2d4f67", "#46647a"],
      horizon: 0.7,
      body: { kind: "moon", x: 0.78, y: 0.2, size: 0.05, color: "#dcd7ba", glow: "#4a6a86" },
      stars: { density: 0.3, color: "#dcd7ba" },
      clouds: { color: "#2a3b52", light: "#3b5068", shade: "#223249", count: 4, at: 0.42, speed: 0.4 },
      layers: [{ kind: "peak", color: "#2d4f67", light: "#3d6380", at: 0.71, height: 0.36, x: 0.36, rough: 0.35, snow: "#dcd7ba" }],
      water: { color: "#1f2b3c", light: "#7fb4ca", at: 0.72 },
      particles: { kind: "petals", color: "#d27e99", density: 0.3 },
    },
  }),
  at({
    id: "p:everforest",
    name: "Everforest",
    blurb: "Morning mist through a quiet forest.",
    mode: "dark",
    font: "clean",
    colors: { bg: "#232a2e", tile: "#2d353b", ink: "#d3c6aa", ink2: "#b8b29a", ink3: "#9da9a0", accent: "#a7c080", ok: "#83c092", warn: "#dbbc7f", bad: "#e67e80", charts: ["#a7c080", "#7fbbb3", "#dbbc7f", "#e69875", "#d699b6"] },
    glass: 0.3,
    blur: 14,
    scene: {
      style: "pixel",
      seed: 64,
      sky: ["#232a2e", "#2d353b", "#3d484d", "#56635f", "#7f8a7a", "#a7a888"],
      horizon: 0.68,
      body: { kind: "sun", x: 0.7, y: 0.5, size: 0.06, color: "#e8d8a8", glow: "#dbbc7f" },
      stars: { density: 0 },
      layers: [
        { kind: "mountains", color: "#56635f", light: "#64716b", at: 0.64, height: 0.13, rough: 0.4 },
        { kind: "forest", color: "#3d484d", light: "#475258", at: 0.72, height: 0.08, trees: { kind: "pine", color: "#343f44", light: "#475258", density: 0.75 } },
        { kind: "forest", color: "#2d353b", light: "#343f44", at: 0.82, height: 0.06, trees: { kind: "pine", color: "#232a2e", light: "#2d353b", density: 0.9 } },
      ],
      fog: { color: "#9da9a0", at: 0.76, amount: 0.65 },
      particles: { kind: "fireflies", color: "#dbbc7f", density: 0.3 },
    },
  }),
  at({
    id: "p:matte-black",
    name: "Matte Black",
    blurb: "Charcoal and a warm amber light.",
    mode: "dark",
    font: "mono",
    colors: { bg: "#0b0b0b", tile: "#141414", ink: "#ececec", ink2: "#a8a8a8", ink3: "#7a7a7a", accent: "#f0a830", ok: "#8ec07c", warn: "#f0a830", bad: "#e46b6b", charts: ["#f0a830", "#d8d8d8", "#8ec07c", "#7aa6d8", "#e46b6b"] },
    glass: 0.08,
    blur: 0,
    radius: 10,
    scene: {
      style: "abstract",
      seed: 9,
      sky: ["#0b0b0b", "#0e0e0e", "#121212"],
      body: { kind: "none" },
      stars: { density: 0 },
      blobs: [
        { x: 0.86, y: -0.08, r: 0.65, color: "#3d2a0c" },
        { x: 0.08, y: 1.08, r: 0.55, color: "#1d1a14" },
      ],
      particles: { kind: "dust", color: "#f0a830", density: 0.1 },
      grain: 0.55,
      vignette: 0.6,
    },
  }),
];

/**
 * Every built-in made theme, in the order the board lists them: the picture
 * themes from the board's own library, then the palettes. The one list, for
 * the code that finds them by id and the board that wears them.
 */
export const BUILT_IN_THEMES: CustomTheme[] = [...PICTURE_THEMES, ...PALETTES];

/** The built-in made themes by id. */
export const PALETTE: Record<string, CustomTheme> = Object.fromEntries(BUILT_IN_THEMES.map((p) => [p.id, p]));
